import FolderBlock, { FolderBlockCommonProps } from '@anm/components/FolderBlock';
import { DragItem, DropZone } from '@anm/components/drag-drop';
import { DragDropContext } from '@anm/contexts/DragOverContext';
import cutFileExtension from '@anm/helpers/cutFileExtension';
import { getDropdownActions } from '@anm/helpers/dropdownActions';
import getFromCdn from '@anm/helpers/getFromCdn';
import useSwitchState from '@anm/hooks/useSwitchState';
import { uploadsActions } from '@anm/store/modules/uploads';
import { memo, useCallback, useContext, useState, DragEvent, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import vtConfig from '../../../config';
import { userSelectors } from '../../../store/modules/user';
import { videoFolderActions, videoFolderSelectors } from '../../../store/modules/videoFolder';
import { ProjectFolderTreeModalContext } from '../ProjectPlayerBlock/ProjectFolderTreeModalProvider';
import useProjectsPermissions from '../hooks/useProjectsPermissions';

import AlreadyInsideAlert from './AlreadyInsideAlert';
import DeleteFolderModal from './DeleteFolderModal';

const {
  uploadVideoDefaults: { metaInfo }
} = vtConfig();

export type ProjectsDragDropPayload = {
  folderId?: number;
  projectId?: string;
};

type ProjectFolderBlockProps = FolderBlockCommonProps & {
  folderId: number;
  parentId: number;
  canFocusField: boolean;
  isRenamed?: boolean;
  onCanFocusField: (v: boolean) => void;
};

const ProjectFolderBlock: FC<ProjectFolderBlockProps> = ({
  title,
  folderId,
  parentId,
  isRenamed,
  onCanFocusField,
  canFocusField: canFocus,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const { canEditProject } = useProjectsPermissions();

  const { openFolderTree, isFolderTreeModal } = useContext(ProjectFolderTreeModalContext);
  const [isAlertOpen, openAlert, closeAlert] = useSwitchState();

  const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useSwitchState();

  const folderPath = useSelector(state => videoFolderSelectors.getFolderPathFor(state, folderId));

  const newFolderState = useSelector(state => videoFolderSelectors.selectNewFolderFor(state, folderId));
  const renameFolderState = useSelector(state => videoFolderSelectors.selectRenameFolderFor(state, folderId));
  const hostingStats = useSelector(userSelectors.selectUserHostingStats)?.data || null;

  const openFolderTreeWithProps = () =>
    !isAlertOpen &&
    openFolderTree({
      parentId,
      moveItemName: 'folder',
      onMoveFolder: handleMoveFolder,
      excludeFolderId: folderId,
      onClose: () => onCanFocusField(false)
    });

  const isInputFocused = !isRenamed && !!canFocus && !isFolderTreeModal && !!newFolderState;

  const [isFieldFocused, focusField] = useState(isInputFocused);

  const isFolderRenaming = renameFolderState?.isPending;

  const onMove = useCallback(() => {
    onCanFocusField(false);
    openFolderTreeWithProps();
  }, []);

  const onDelete = openDeleteModal;
  const onRename = useCallback(
    (name: string) => name && dispatch(videoFolderActions.renameFolder({ folderId, name })),
    [folderId]
  );

  const handleMoveFolder = useCallback(
    (toParentId: number) => {
      return toParentId === parentId ? openAlert() : dispatch(videoFolderActions.moveFolder({ folderId, toParentId }));
    },
    [folderId, parentId]
  );

  const { isDragOver, setIsDragOver, setIsRootDragOver } = useContext(DragDropContext);

  const handleCanDrop = useCallback((payload: ProjectsDragDropPayload) => payload?.folderId !== folderId, [folderId]);

  const user = useSelector(userSelectors.selectUser);

  const uploadFile = useCallback(
    (file: File) => {
      const name = cutFileExtension(file?.name);
      const description = `${name} - ${metaInfo.description}`;

      dispatch(
        uploadsActions.createUpload({
          user: user.profile,
          isUploadFile: true,
          hostingStats,
          uploads: [
            {
              file,
              folderId,
              meta: { name, description }
            }
          ]
        })
      );
    },
    [folderId]
  );

  const moveProject = useCallback(
    (projectId: string) =>
      dispatch(
        videoFolderActions.moveProject({
          folderId,
          projectId
        })
      ),
    [folderId]
  );

  const moveFolder = useCallback(
    (folderId: number, toParentId: number) =>
      dispatch(
        videoFolderActions.moveFolder({
          folderId,
          toParentId
        })
      ),
    []
  );

  const handleDropped = useCallback(
    (payload: ProjectsDragDropPayload, event: DragEvent<HTMLDivElement>) => {
      setIsDragOver(false);
      setIsRootDragOver(false);
      const canMoveFolder = payload?.folderId && payload?.folderId !== folderId;

      const file = event.dataTransfer.files[0];

      file && uploadFile(file);
      canMoveFolder && moveFolder(payload.folderId!, folderId);
      payload?.projectId && moveProject(payload?.projectId);
    },
    [uploadFile, moveFolder, moveProject]
  );

  const handleDragOver = useCallback(() => {
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((evt: DragEvent<HTMLDivElement>) => {
    const relativeTargetId = (evt.relatedTarget as HTMLDivElement)?.id;

    const allowedIds = ['masonry-wrapper', 'masonry-column', 'videos-content', 'infinite-scroll-list'];
    const canHide = allowedIds.includes(relativeTargetId);

    canHide && setIsDragOver(false);
  }, []);

  return (
    <>
      {isAlertOpen && <AlreadyInsideAlert {...{ folderId, parentId }} onClose={closeAlert} />}
      {isDeleteModalOpen && (
        <DeleteFolderModal {...{ folderId }} onClose={closeDeleteModal} onDelete={closeDeleteModal} />
      )}
      <DropZone<ProjectsDragDropPayload>
        canDrop={handleCanDrop}
        onDropped={handleDropped}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        canShowDropZone={isDragOver}
      >
        <DragItem<ProjectsDragDropPayload>
          draggable={!isFieldFocused}
          payload={{ folderId }}
          dragPreview={getFromCdn('images/dashboard/folder-drag-preview.svg')}
        >
          <FolderBlock
            title={isFolderRenaming ? 'renaming...' : title}
            isPending={isFolderRenaming}
            titleVariant={isFolderRenaming ? 'gray' : 'black'}
            onFieldFocused={focusField}
            linkProps={{
              route: 'my-projects-folder',
              params: {
                ...(folderPath && { folderPath })
              }
            }}
            actions={getDropdownActions({ onMove, onDelete })}
            canEdit={canEditProject}
            {...{
              onRename,
              isInputFocused,
              ...otherProps
            }}
          />
        </DragItem>
      </DropZone>
    </>
  );
};

export const ProjectFolderBlockMemorized = memo(ProjectFolderBlock);

export default ProjectFolderBlock;
